import { useCallback } from 'react';
import styled from 'styled-components';

import ADD_USER_DAMAGE from 'components/damages/DamageEdit/AddUserDamage.gql';
import { useDamageAttrs } from 'components/damages/withDamages';
import RESET_DAMAGE_ATTRS from 'horizon/queries/ResetDamageAttrs.gql';
import { DAMAGE_EDIT_ATTRIBUTES } from 'utils/access-control/rules';
import { useMutation } from 'utils/apollo';
import { usePermissions } from 'utils/usePermissions';

import { DamageAttributes, MODES } from './DamageAttributes';

const AttributeContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1;
`;

const StyledAttributes = styled(DamageAttributes)`
  display: flex;
  width: 100%;
  transition: margin-left ease-out 0.2s;
  overflow: hidden;
  white-space: nowrap;
  ${({ currentMode, mode }) =>
    currentMode !== mode ? 'margin-left: 100%; position: absolute; z-index: -1;' : ''};
`;

export function DamageAttributesController({
  damage,
  schemaId,
  potentialAttrs,
  loading: loadingProp,
  mode,
  setMode,
  hideDetails,
}) {
  const { canAccess } = usePermissions();

  const { id: damageId } = damage || {};

  const { damageColumns, loading: loadingAttrs } = useDamageAttrs(schemaId);

  const [onSave, { loading: saving }] = useMutation(ADD_USER_DAMAGE, {
    onCompleted() {
      setMode(MODES.VIEW);
    },
  });

  const [onReset, { loading: resetting }] = useMutation(RESET_DAMAGE_ATTRS);

  const handleOnSave = useCallback(
    ({ attrs, schemaId }) => {
      const {
        observations: [
          {
            id: observationId,
            coordinates,
            picture: { id: pictureId },
          },
        ],
      } = damage;
      return onSave({
        variables: {
          damage: {
            branchGlobalId: damageId,
            schemaId,
            attrs,
            annotations: [
              {
                branchGlobalId: observationId,
                coordinates,
                pictureId,
              },
            ],
          },
        },
      });
    },
    [onSave, damage, damageId]
  );

  const handleOnReset = useCallback(() => {
    onReset({ variables: { branchGlobalId: damageId } });
  }, [onReset, damageId]);

  const handleOnCancel = useCallback(() => setMode(MODES.VIEW), [setMode]);

  const loading = loadingProp || loadingAttrs;

  const props = {
    currentMode: mode,
    loading,
    damage,
    schemaId,
    potentialAttrs,
    damageColumns,
    onSave: handleOnSave,
    onReset: !hideDetails ? handleOnReset : null,
    saving: saving || resetting,
    onCancel: handleOnCancel,
  };

  // both modes are rendered in this usage so we can animate between the state change
  return (
    <AttributeContainer>
      <StyledAttributes data-testid="attr-view" {...props} mode={MODES.VIEW} />
      {canAccess([DAMAGE_EDIT_ATTRIBUTES]) && (
        <StyledAttributes data-testid="attr-edit" {...props} mode={MODES.EDIT} />
      )}
    </AttributeContainer>
  );
}
